import { useEffect, useState } from "react";
import client, { apiError } from "../../api/client";
import { toast } from "sonner";
import { Plus, Trash2, X, Loader2 } from "lucide-react";

const inputCls = "w-full h-9 px-3 rounded-md border border-input bg-background text-sm outline-none focus:ring-2 focus:ring-ring/20";
const ROLES = [["admin", "Administrátor"], ["user", "Uživatel / koordinátor"], ["lector", "Lektor"]];
const EMPTY = { username: "", email: "", name: "", role: "user", password: "", phone: "", active: true };

export default function UsersManager() {
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => client.get("/admin/users").then((res) => setUsers(res.data));
  useEffect(() => { load(); }, []);

  const open = (u) => { setForm(u ? { ...u, password: "" } : EMPTY); setModal(u ? "edit" : "new"); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || !form.email) return toast.error("Vyplňte jméno a e-mail");
    setSaving(true);
    try {
      if (modal === "new") {
        if (!form.username || !form.password) { setSaving(false); return toast.error("Vyplňte přihlašovací jméno a heslo"); }
        await client.post("/admin/users", form);
      } else {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await client.put(`/admin/users/${form.id}`, payload);
      }
      toast.success("Uloženo"); setModal(null); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`Smazat uživatele ${u.name}?`)) return;
    try { await client.delete(`/admin/users/${u.id}`); load(); } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-medium">Uživatelé</h2>
        <button onClick={() => open(null)} data-testid="add-user-btn" className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 flex items-center gap-1"><Plus className="h-4 w-4" /> Nový uživatel</button>
      </div>
      <div className="border border-border rounded-lg divide-y divide-border">
        {users.map((u) => (
          <div key={u.id} className="flex items-center gap-3 px-4 py-3" data-testid={`user-row-${u.id}`}>
            <div className="flex-1">
              <div className="text-sm font-medium">{u.name} {!u.active && <span className="text-xs text-muted-foreground">(neaktivní)</span>}</div>
              <div className="text-xs text-muted-foreground">{u.username} · {u.email}</div>
            </div>
            <span className="text-xs px-2 py-1 rounded-full bg-secondary">{ROLES.find((r) => r[0] === u.role)?.[1]}</span>
            <button onClick={() => open(u)} className="text-xs text-muted-foreground hover:text-foreground px-2">Upravit</button>
            <button onClick={() => del(u)} className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onMouseDown={() => setModal(null)}>
          <div className="bg-background rounded-lg border border-border shadow-lg w-full max-w-md" onMouseDown={(e) => e.stopPropagation()} data-testid="user-modal">
            <div className="flex items-center justify-between px-5 h-14 border-b border-border">
              <h3 className="font-heading font-semibold">{modal === "new" ? "Nový uživatel" : "Upravit uživatele"}</h3>
              <button onClick={() => setModal(null)}><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div><label className="text-xs text-muted-foreground">Jméno</label><input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="user-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-muted-foreground">Přihlašovací jméno</label><input value={form.username} onChange={(e) => set("username", e.target.value)} className={inputCls} data-testid="user-username" /></div>
                <div><label className="text-xs text-muted-foreground">E-mail</label><input value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="user-email" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-muted-foreground">Telefon</label><input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></div>
                <div><label className="text-xs text-muted-foreground">Role</label>
                  <select value={form.role} onChange={(e) => set("role", e.target.value)} className={inputCls} data-testid="user-role">
                    {ROLES.map((r) => <option key={r[0]} value={r[0]}>{r[1]}</option>)}
                  </select>
                </div>
              </div>
              <div><label className="text-xs text-muted-foreground">Heslo {modal === "edit" && "(ponechte prázdné pro zachování)"}</label><input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className={inputCls} data-testid="user-password" /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} /> Aktivní účet</label>
            </div>
            <div className="px-5 py-4 border-t border-border flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="h-9 px-4 rounded-md border border-border text-sm hover:bg-secondary">Zrušit</button>
              <button onClick={save} disabled={saving} data-testid="user-save" className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-60 flex items-center gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Uložit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
